/* ==========================================================================
   FILE: modules/identity/components/BlockchainStatus.jsx
   IAMONEAI - Blockchain Verification Status Component
   Displays blockchain anchoring status and transaction details
   ========================================================================== */

import React, { useState } from 'react';
import { Button } from '../../../global/components';
import './BlockchainStatus.css';

// ==========================================================================
// BLOCKCHAIN STATUS COMPONENT
// ==========================================================================

function BlockchainStatus({
  isAnchored = false,
  txHash,
  blockNumber,
  network = 'Polygon Amoy',
  anchoredAt,
  identityHash,
  onAnchor,
  isAnchoring = false,
  className = '',
}) {
  const [showDetails, setShowDetails] = useState(false);

  const statusClasses = [
    'blockchain-status',
    isAnchored ? 'blockchain-status-verified' : 'blockchain-status-pending',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={statusClasses}>
      {/* Header */}
      <div className="blockchain-status-header">
        <div className="blockchain-status-indicator">
          <div
            className={`blockchain-status-dot ${
              isAnchored
                ? 'blockchain-status-dot-verified'
                : 'blockchain-status-dot-pending'
            }`}
          >
            {isAnchored ? <CheckIcon /> : <ClockIcon />}
          </div>
          <div className="blockchain-status-info">
            <h4 className="blockchain-status-title">
              {isAnchored ? 'Blockchain Verified' : 'Pending Verification'}
            </h4>
            <p className="blockchain-status-network">
              {network} {isAnchored ? '• Confirmed' : '• Awaiting anchor'}
            </p>
          </div>
        </div>

        {isAnchored && (
          <button
            className="blockchain-details-toggle"
            onClick={() => setShowDetails(!showDetails)}
            aria-expanded={showDetails}
          >
            {showDetails ? 'Hide' : 'Details'}
            <ChevronIcon direction={showDetails ? 'up' : 'down'} />
          </button>
        )}
      </div>

      {/* Not Anchored */}
      {!isAnchored && (
        <div className="blockchain-status-content">
          <div className="blockchain-pending-info">
            <ShieldIcon />
            <div>
              <p>Your identity is not yet anchored to the blockchain.</p>
              <p className="blockchain-pending-hint">
                Anchoring creates an immutable record of your identity that can
                never be altered or deleted.
              </p>
            </div>
          </div>

          {onAnchor && (
            <Button
              variant="primary"
              onClick={onAnchor}
              loading={isAnchoring}
              disabled={isAnchoring}
              className="blockchain-anchor-btn"
            >
              <ChainIcon />
              {isAnchoring ? 'Anchoring...' : 'Anchor Identity'}
            </Button>
          )}
        </div>
      )}

      {/* Anchored Details */}
      {isAnchored && showDetails && (
        <div className="blockchain-status-details">
          {txHash && (
            <BlockchainDetail
              label="Transaction Hash"
              value={txHash}
              copyable
              link={getExplorerLink(network, 'tx', txHash)}
            />
          )}

          {blockNumber && (
            <BlockchainDetail
              label="Block Number"
              value={blockNumber}
              link={getExplorerLink(network, 'block', blockNumber)}
            />
          )}

          {identityHash && (
            <BlockchainDetail
              label="Identity Hash"
              value={identityHash}
              copyable
              truncate
            />
          )}

          {anchoredAt && (
            <BlockchainDetail
              label="Anchored At"
              value={formatDate(anchoredAt)}
            />
          )}

          <div className="blockchain-explorer-link">
            <a
              href={getExplorerLink(network, 'tx', txHash)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLinkIcon />
              View on {getExplorerName(network)}
            </a>
          </div>
        </div>
      )}

      {/* Verification badge */}
      {isAnchored && !showDetails && (
        <div className="blockchain-verified-badge">
          <VerifiedIcon />
          <span>Identity cryptographically verified on {network}</span>
        </div>
      )}
    </div>
  );
}

// ==========================================================================
// BLOCKCHAIN DETAIL COMPONENT
// ==========================================================================

function BlockchainDetail({ label, value, copyable = false, truncate = false, link }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const displayValue =
    truncate && value?.length > 20
      ? `${value.slice(0, 10)}...${value.slice(-10)}`
      : value;

  return (
    <div className="blockchain-detail">
      <span className="blockchain-detail-label">{label}</span>

      <div className="blockchain-detail-value-wrapper">
        {link ? (
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="blockchain-detail-link"
          >
            <code>{displayValue}</code>
            <ExternalLinkIcon />
          </a>
        ) : (
          <code>{displayValue}</code>
        )}

        {copyable && (
          <button
            className={`blockchain-detail-copy ${
              copied ? 'blockchain-detail-copy-success' : ''
            }`}
            onClick={handleCopy}
            aria-label={copied ? 'Copied!' : 'Copy'}
          >
            {copied ? <CheckIcon /> : <CopyIcon />}
          </button>
        )}
      </div>
    </div>
  );
}

// ==========================================================================
// COMPACT BADGE
// ==========================================================================

export function BlockchainBadge({
  isAnchored,
  network = 'Polygon',
  onClick,
  className = '',
}) {
  const badgeClasses = [
    'blockchain-badge',
    isAnchored ? 'blockchain-badge-verified' : 'blockchain-badge-pending',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button className={badgeClasses} onClick={onClick}>
      {isAnchored ? <VerifiedIcon /> : <ClockIcon />}
      <span>{isAnchored ? 'Verified' : 'Pending'}</span>
      {isAnchored && (
        <span className="blockchain-badge-network">{network}</span>
      )}
    </button>
  );
}

// ==========================================================================
// HELPERS
// ==========================================================================

function getExplorerLink(network, type, value) {
  if (!value) return '#';

  const explorers = {
    'Polygon Amoy': 'https://amoy.polygonscan.com',
    Polygon: 'https://polygonscan.com',
    Ethereum: 'https://etherscan.io',
  };

  const baseUrl = explorers[network] || explorers['Polygon Amoy'];
  return `${baseUrl}/${type}/${value}`;
}

function getExplorerName(network) {
  const names = {
    'Polygon Amoy': 'Amoy Polygonscan',
    Polygon: 'Polygonscan',
    Ethereum: 'Etherscan',
  };
  return names[network] || 'Block Explorer';
}

function formatDate(dateString) {
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
}

// ==========================================================================
// ICONS
// ==========================================================================

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function ChainIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function VerifiedIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function ExternalLinkIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

function ChevronIcon({ direction = 'up' }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      style={{
        transform: direction === 'up' ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: 'transform 0.2s',
      }}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

// ==========================================================================
// EXPORT
// ==========================================================================

export default BlockchainStatus;
